import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useAddressIssue } from "@/hooks/useValidation";
import type {
  ValidationIssue,
  ResolutionType,
  ResolutionRequest,
} from "@/types/validation";
import { toast } from "sonner";

interface ResolveIssueDialogProps {
  issue: ValidationIssue | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ResolveIssueDialog({
  issue,
  open,
  onOpenChange,
  onSuccess,
}: ResolveIssueDialogProps) {
  const [resolutionType, setResolutionType] = useState<ResolutionType>("fixed");
  const [resolutionNotes, setResolutionNotes] = useState("");

  const addressMutation = useAddressIssue();

  if (!issue) return null;

  const handleResolve = async () => {
    try {
      const resolution: ResolutionRequest = {
        status: "resolved",
        resolution_type: resolutionType,
      };
      if (resolutionNotes.trim()) {
        resolution.resolution_notes = resolutionNotes.trim();
      }
      await addressMutation.mutateAsync({
        issueId: issue.id,
        resolution,
      });
      toast.success("Issue marked as resolved");
      setResolutionNotes("");
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(
        `Failed to resolve issue: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setResolutionNotes("");
      setResolutionType("fixed");
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Resolve validation issue</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground line-clamp-2">
            {issue.message}
          </p>
          <div>
            <Label htmlFor="resolve-resolution-type">Resolution type</Label>
            <Select
              value={resolutionType}
              onValueChange={(v) => setResolutionType(v as ResolutionType)}
            >
              <SelectTrigger id="resolve-resolution-type" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="false_positive">False positive</SelectItem>
                <SelectItem value="wont_fix">Won't fix</SelectItem>
                <SelectItem value="needs_investigation">
                  Needs investigation
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="resolve-notes">Notes (optional)</Label>
            <Textarea
              id="resolve-notes"
              placeholder="Add any notes about how this was resolved..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              className="mt-1"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleResolve} disabled={addressMutation.isPending}>
            {addressMutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Resolve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
